import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send } from 'lucide-react';
interface ChatMessage {
  from: 'bot' | 'user';
  text: string;
}
const INITIAL_MESSAGES: ChatMessage[] = [{
  from: 'bot',
  text: "👋 Hi there! I'm Hira from Hirelio. How can we help accelerate your career today?"
}];
const QUICK_REPLIES = ['Free resume review', 'Turnaround time', 'Talk to an expert'];
export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(INITIAL_MESSAGES);
  const [input, setInput] = useState('');
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, open]);
  const sendMessage = (text: string) => {
    if (!text.trim()) return;
    setMessages(prev => [...prev, {
      from: 'user',
      text
    }]);
    setInput('');
    setTimeout(() => {
      setMessages(prev => [...prev, {
        from: 'bot',
        text: 'Thanks for reaching out! A career specialist will follow up by email shortly. In the meantime, feel free to start with a free resume review.'
      }]);
    }, 800);
  };
  return <>
      {/* Toggle button */}
      <button onClick={() => setOpen(o => !o)} aria-label={open ? 'Close chat' : 'Open chat'} aria-expanded={open} className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-brand hover:bg-brand-600 text-white shadow-xl shadow-brand/30 flex items-center justify-center transition-all active:scale-95" data-id="element-13">
        <AnimatePresence mode="wait" initial={false} data-id="element-14">
          {open ? <motion.span key="close" initial={{
          rotate: -90,
          opacity: 0
        }} animate={{
          rotate: 0,
          opacity: 1
        }} exit={{
          rotate: 90,
          opacity: 0
        }} transition={{
          duration: 0.15
        }} data-id="element-15">
              <X size={24} data-id="element-16" />
            </motion.span> : <motion.span key="open" initial={{
          rotate: 90,
          opacity: 0
        }} animate={{
          rotate: 0,
          opacity: 1
        }} exit={{
          rotate: -90,
          opacity: 0
        }} transition={{
          duration: 0.15
        }} data-id="element-17">
              <MessageCircle size={24} data-id="element-18" />
            </motion.span>}
        </AnimatePresence>
      </button>

      {/* Chat panel */}
      <AnimatePresence data-id="element-19">
        {open && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.96
      }} transition={{
        duration: 0.2
      }} role="dialog" aria-label="Live chat with Hirelio" className="fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] max-w-sm bg-white dark:bg-navy-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-navy-700 overflow-hidden flex flex-col" data-id="element-20">
            {/* Header */}
            <div className="bg-navy-900 text-white p-4 flex items-center gap-3" data-id="element-21">
              <div className="w-10 h-10 rounded-full bg-brand flex items-center justify-center font-heading font-bold" data-id="element-22">
                H
              </div>
              <div data-id="element-23">
                <p className="font-heading font-semibold leading-tight" data-id="element-24">
                  Hirelio Support
                </p>
                <p className="text-xs text-slate-300 flex items-center gap-1.5" data-id="element-25">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" data-id="element-26" />
                  Typically replies in minutes
                </p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 p-4 space-y-3 overflow-y-auto max-h-80 bg-slate-50 dark:bg-navy-900/50" data-id="element-27">
              {messages.map((m, i) => <div key={i} className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-28">
                  <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.from === 'user' ? 'bg-brand text-white rounded-br-md' : 'bg-white dark:bg-navy-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-navy-700 rounded-bl-md'}`} data-id="element-29">
                    {m.text}
                  </div>
                </div>)}

              {messages.length === 1 && <div className="flex flex-wrap gap-2 pt-1" data-id="element-30">
                  {QUICK_REPLIES.map(q => <button key={q} onClick={() => sendMessage(q)} className="text-xs px-3 py-1.5 rounded-full border border-brand/40 text-brand hover:bg-brand-50 dark:hover:bg-navy-800 transition-colors" data-id="element-31">
                      {q}
                    </button>)}
                </div>}
              <div ref={endRef} data-id="element-32" />
            </div>

            {/* Input */}
            <form onSubmit={e => {
          e.preventDefault();
          sendMessage(input);
        }} className="p-3 border-t border-slate-200 dark:border-navy-700 flex items-center gap-2 bg-white dark:bg-navy-800" data-id="element-33">
              <input value={input} onChange={e => setInput(e.target.value)} placeholder="Type your message..." aria-label="Type your message" className="flex-1 px-4 py-2.5 rounded-full border border-slate-200 dark:border-navy-600 bg-slate-50 dark:bg-navy-900 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-brand/50 focus:border-brand outline-none transition-all" data-id="element-34" />
              <button type="submit" aria-label="Send message" className="w-10 h-10 rounded-full bg-brand hover:bg-brand-600 text-white flex items-center justify-center shrink-0 transition-colors" data-id="element-35">
                <Send size={18} data-id="element-36" />
              </button>
            </form>
          </motion.div>}
      </AnimatePresence>
    </>;
}